/* eslint-disable camelcase */
import React, { useState, useEffect } from "react";
import { injectIntl, intlShape } from "react-intl";

import { useSelector, useDispatch } from "react-redux";
import generateAction from "../../../utils/action-helpers";

import { CONCILIATED } from "../../../state/actions";

import Table from "../../ui/table";
import Loader from "../../ui/loader";
import { originStatusName, sapStatusName } from "./utils";
import ExpandedRow from "./expanded-row";
import ExpandButton from "./expand-button";
import { toFormattedNumber, toHibridSocietyFormat } from "../../../utils/format";
import ConciliationStatus from "./concilitation-status";
import TooltipDiv from "../../ui/tooltip-div";
import { calculateCurency } from "../reconciliation-detail";

const mapper = ({ transfer, proposal, loadedItems, loading, error }) => ({
  kukey: transfer.kukey,
  record: transfer.record,
  items: proposal.items,
  sapCode: proposal.tercero,
  name: transfer.name,
  userRule: proposal.user === "ALGORITHM" ? proposal.rule : proposal.user,
  amount: transfer.amount,
  accountNumber: transfer.accountNumber,
  currency: transfer.currency,
  sapStatus: transfer.sapStatus,
  concStatus: transfer.concStatus,
  auxAccountNumber: transfer.auxAccountNumber,
  society: transfer.society ? transfer.society : "",
  itemsObject: {
    items: loadedItems
      ? loadedItems.map(
          ({
            FX_DOCUMENT_DATE: date,
            FX_EXPIRATION_NET: expiringDate,
            CO_SOCIETY: society,
            CO_DIVISION: division,
            CO_CLAIM_AREA: recArea,
            DE_POSITION_TEXT: comments,
            DE_REFERENCE_DOCUMENT: documentParam,
            CO_ACCOUNTING_KEY: accountingKey,
            AM_LOCAL_CURRENCY: amount,
            AM_FOREIGN_CURRENCY: amountForeign,
            DE_CUSTOMER_NAME: customerName,
            ID_CUSTOMER: customerId
          }) => ({
            date,
            expiringDate,
            society,
            division,
            recArea,
            currency: transfer.currency,
            comments,
            customer: proposal.name,
            document: documentParam,
            accountingKey,
            amount,
            customerName,
            amountForeign,
            customerId
          })
        )
      : undefined,
    paymentsClient: proposal.paymentsClient ? proposal.paymentsClient : [],
    paymentsCount: proposal.paymentsCount ? proposal.paymentsCount : [],
    loading,
    error
  }
});

const Pending = ({ intl }) => {
  const { messages } = intl;

  const [expandedRows, setExpandedRows] = useState([]);

  const { loading, error, conciliated, filters, pageIndex } = useSelector(
    state => state.conciliated
  );
  const dispatch = useDispatch();

  useEffect(() => {
    if (!conciliated && !loading && !error) {
      dispatch(generateAction(CONCILIATED.FETCH));
    }
  }, [loading, error, conciliated, dispatch]);

  if (loading) return <Loader />;

  const toggleRow = row => {
    const newRows = expandedRows.slice(0, expandedRows.length);
    if (newRows.includes(row.original.myTableIndex)) {
      newRows.splice(newRows.indexOf(row.original.myTableIndex), 1);
    } else {
      if (
        !row.original.itemsObject ||
        (!row.original.itemsObject.items && !row.original.itemsObject.loading)
      ) {
        const { kukey, record, items } = row.original;
        dispatch(generateAction(CONCILIATED.GET_ITEMS, { kukey, record, items }));
      }
      newRows.push(row.original.myTableIndex);
    }
    setExpandedRows(newRows);
  };

  const columns = [
    {
      name: "concStatus",
      label: messages.origen,
      options: {
        show: true,
        filter: true,
        cellRenderer: value => {
          const { concStatus } = value.row.original;
          return <ConciliationStatus concStatus={concStatus || 0} />;
        },
        filterRenderer: value => originStatusName(value)
      }
    },
    {
      name: "sapCode",
      label: messages.sapCode,
      options: {
        show: true
      }
    },
    {
      name: "name",
      label: messages.name
    },
    {
      name: "society",
      label: messages.society,
      options: {
        filter: true,
        show: false,
        cellRenderer: value => toHibridSocietyFormat(value.row.original.society),
        filterRenderer: value => toHibridSocietyFormat(value)
      }
    },
    {
      name: "accountNumber",
      label: messages.account,
      options: {
        filter: true,
        cellRenderer: value => {
          const { accountNumber, auxAccountNumber } = value.row.original;
          return (
            <TooltipDiv
              containerClassName="mr-2"
              text={accountNumber}
              tooltipText={`Cuenta Auxiliar: ${auxAccountNumber}`}
            />
          );
        }
      }
    },
    {
      name: "userRule",
      label: messages.userRule,
      options: {
        filter: true,
        show: true
      }
    },
    {
      name: "currrency",
      label: "",
      options: {
        filter: false,
        show: false
      }
    },
    {
      name: "auxAccountNumber",
      label: messages.auxAccountNumber,
      options: {
        filter: true,
        show: false
      }
    },
    {
      name: "amount",
      label: messages.amount,
      options: {
        type: "number",
        show: true,
        cellRenderer: value => {
          const { amount, currency } = value.row.original;
          const currencyText = currency ? calculateCurency(currency) : "€";
          return (
            <div className="text-right mr-2">{`${toFormattedNumber(amount)}${currencyText}`}</div>
          );
        }
      }
    },
    {
      name: "sapStatus",
      label: messages.sapStatus,
      options: {
        show: true,
        filter: true,
        cellRenderer: value => {
          const { sapStatus } = value.row.original;
          return <ConciliationStatus sapStatus={sapStatus || 0} />;
        },
        filterRenderer: value => sapStatusName(value)
      }
    },
    {
      label: messages.empty,
      options: {
        sort: false,
        cellClass: "short-column",
        cellRenderer: value => (
          <ExpandButton
            toggleRow={() => toggleRow(value.row)}
            isExpanded={expandedRows.includes(value.row.original.myTableIndex)}
          />
        )
      }
    },
    {
      name: "itemsObject",
      label: messages.empty,
      options: {
        sort: false,
        filter: false,
        expandedColumn: true,
        cellRenderer: element => {
          let list = element.cell.value.items ? element.cell.value.items : [];
          list = list.concat(element.cell.value.paymentsClient || []);
          list = list.concat(element.cell.value.paymentsCount || []);
          return (
            <ExpandedRow
              currency={element.row.original.currency}
              list={list}
              loading={element.cell.value.loading}
              error={element.cell.value.error}
              columnsLength={columns.length}
            />
          );
        }
      }
    }
  ];
  const setFilters = newFilters => dispatch(generateAction(CONCILIATED.SET_FILTER, newFilters));
  const setPageIndex = newPageIndex =>
    dispatch(generateAction(CONCILIATED.SET_PAGE_INDEX, newPageIndex));
  const tableOptions = {
    expandedRows,
    emptyElement: <span>No hay elementos conciliados en el día de hoy</span>,
    emptyFilteredElement: (
      <span>No hay resultados que coincidan con los filtros de tu búsqueda</span>
    ),
    remoteSelectedFilters: filters,
    setRemoteSelectedFilters: setFilters,
    remotePageIndex: pageIndex,
    setRemotePageIndex: setPageIndex
  };
  return (
    <Table
      placeHolder="Buscar por ordenante, importe..."
      fileName="Conciliadas.csv"
      className="reconciled"
      columns={columns}
      data={conciliated ? conciliated.map(mapper) : []}
      options={tableOptions}
      loading={loading}
    />
  );
};

Pending.propTypes = {
  intl: intlShape.isRequired
};

export default injectIntl(Pending);
